import {
  normalizeEntityName,
  rankEntityNameMatches,
} from './entityNameSimilarity';
import { buildCandidateKey } from '../features/reviewableItems/candidateKeys';
import { readCanonicalMedicationRecord } from '../features/medications/canonicalMedicationRecord';
import {
  buildReviewableCandidateShell,
  buildReviewableOrigin,
  REVIEWABLE_ORIGIN_COLLECTIONS,
  REVIEWABLE_ORIGIN_TYPES,
} from '../features/reviewableItems/reviewableItemFlow';

const normalizeText = (value = '') => String(value || '').trim();
export const MEDICATION_REVIEW_DOMAIN = 'medication';
export const MEDICATION_REVIEW_ALLOWED_ACTIONS = ['link_existing', 'add_new', 'update_existing', 'temporary_hold', 'discontinue', 'complete_course', 'historical_snapshot', 'add_current_snapshot', 'ignore'];

const MEDICATION_DOSE_PATTERN = /\b(\d+(?:\.\d+)?)\s*(mg|mcg|g|ml|mL|units?|iu|IU|caps?|capsules?|tabs?|tablets?)\b/;

export const parseMedicationDoseText = (value = '') => {
  const text = normalizeText(value);
  if (!text) {
    return { dose: '', unit: '' };
  }

  const match = text.match(MEDICATION_DOSE_PATTERN);
  if (!match) {
    return { dose: text, unit: '' };
  }

  return {
    dose: match[1] || '',
    unit: match[2] || '',
  };
};

export const cleanMedicationCandidateName = (value = '') => normalizeText(value)
  .replace(MEDICATION_DOSE_PATTERN, ' ')
  .replace(/\b(mg|mcg|g|ml|mL|units?|iu|IU|caps?|capsules?|tabs?|tablets?|pill|pills)\b/gi, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const normalizeComparableText = (value = '') => normalizeText(value)
  .toLowerCase()
  .replace(/[^\w\s.]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const normalizeMedicationName = (value = '') => normalizeEntityName(value)
  .replace(MEDICATION_DOSE_PATTERN, ' ')
  .replace(/\b(mg|mcg|g|ml|units?|iu|caps?|capsules?|tabs?|tablets?|pill|pills)\b/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const getMedicationSourceNameCandidates = (value = '') => {
  const normalizedSource = normalizeText(value)
    .replace(MEDICATION_DOSE_PATTERN, ' ')
    .replace(/\b(?:gave|give|given|took|take|taken|administered|medication|medicine|meds?|dose|usual|morning|afternoon|evening|bedtime|today|tonight|am|pm)\b/gi, ' ')
    .replace(/[^a-zA-Z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const tokens = normalizedSource.split(/\s+/).filter((token) => token.length >= 4);
  const candidates = new Set(tokens);
  for (let index = 0; index < tokens.length - 1; index += 1) {
    candidates.add(`${tokens[index]} ${tokens[index + 1]}`);
  }

  return [...candidates];
};

const getMedicationResolutionNames = (medication = {}) => [
  medication.name,
  medication.medicationName,
  medication.genericName,
  medication.brandName,
  ...(Array.isArray(medication.aliases) ? medication.aliases : []),
].filter(Boolean);

const rankMedicationMatchesForName = ({
  name = '',
  medications = [],
  limit = 4,
  minSimilarity = 0.74,
} = {}) => rankEntityNameMatches({
  extractedName: name,
  entities: medications,
  getNames: getMedicationResolutionNames,
  minSimilarity,
  limit,
});

const rankMedicationMatchesForSourceText = ({ sourceText = '', medications = [], limit = 4 } = {}) => {
  const matchesByMedication = new Map();
  getMedicationSourceNameCandidates(sourceText).forEach((candidateName) => {
    rankMedicationMatchesForName({
      name: candidateName,
      medications,
      limit,
      minSimilarity: 0.68,
    }).forEach((match) => {
      const key = match.entity.id || match.entity.name;
      const previous = matchesByMedication.get(key);
      if (!previous || match.score > previous.score) {
        matchesByMedication.set(key, {
          ...match,
          extractedName: cleanMedicationCandidateName(candidateName),
        });
      }
    });
  });

  return [...matchesByMedication.values()]
    .sort((left, right) => right.score - left.score)
    .slice(0, limit);
};

export const buildMedicationReviewCandidateFromResolution = (resolution = {}) => {
  const extractedItem = resolution.extractedItem || {};
  const originType = resolution.originType || REVIEWABLE_ORIGIN_TYPES.TIMELINE_ENTRY;
  const originId = resolution.originId || '';
  const childId = resolution.childId || '';
  const name = normalizeText(extractedItem.name);
  const sourceText = normalizeText(extractedItem.sourceText);
  const shell = originId
    ? buildReviewableCandidateShell({
      origin: buildReviewableOrigin({
        type: originType,
        id: originId,
        collection: resolution.originCollection || REVIEWABLE_ORIGIN_COLLECTIONS.DAILY_LOGS,
        childId,
        sourceText,
      }),
      domain: MEDICATION_REVIEW_DOMAIN,
      label: name,
      sourceText,
      allowedActions: MEDICATION_REVIEW_ALLOWED_ACTIONS,
      suggestedAction: 'add_new',
      confidence: resolution.confidence || '',
    })
    : {
      schemaVersion: 1,
      key: buildCandidateKey({
        originId,
        domain: MEDICATION_REVIEW_DOMAIN,
        label: name,
        sourceAnchor: sourceText,
      }),
      origin: {
        type: originType,
        id: originId,
        collection: resolution.originCollection || '',
        childId,
      },
      domain: MEDICATION_REVIEW_DOMAIN,
      label: name,
      sourceText,
      confidence: resolution.confidence || '',
      needsReview: true,
      suggestedAction: 'add_new',
      allowedActions: MEDICATION_REVIEW_ALLOWED_ACTIONS,
    };

  return {
    ...shell,
    candidateKey: shell.candidateKey || shell.key,
    originType,
    originId,
    childId,
    resolutionType: resolution.resolutionType || 'medication',
    resolutionStatus: resolution.resolutionStatus || 'pending',
    reviewReasons: Array.isArray(resolution.reviewReasons) ? resolution.reviewReasons : [],
    extracted: {
      name,
      dose: normalizeText(extractedItem.dose),
      unit: normalizeText(extractedItem.unit),
      rawDose: normalizeText(extractedItem.rawDose),
      time: normalizeText(extractedItem.time),
      scheduleText: normalizeText(extractedItem.scheduleText || extractedItem.time),
      sourceText,
      status: normalizeText(extractedItem.status),
      reason: normalizeText(extractedItem.reason),
    },
  };
};

export const buildCareSourceMedicationReviewCandidate = ({ candidate = {}, source = {} } = {}) => {
  const suggestion = candidate.suggestion || {};
  const nextMedication = candidate.nextMedication || {};
  const schedules = Array.isArray(nextMedication.schedules) ? nextMedication.schedules : [];
  const primarySchedule = schedules[0] || {};
  const dose = normalizeText(primarySchedule.dose || nextMedication.dose || suggestion.dose);
  const unit = normalizeText(primarySchedule.unit || nextMedication.unit || suggestion.unit);
  const time = normalizeText(primarySchedule.time || nextMedication.time);
  const scheduleText = normalizeText(
    nextMedication.sourceScheduleText
    || nextMedication.schedule
    || suggestion.schedule
    || time
  );
  const originType = REVIEWABLE_ORIGIN_TYPES.CARE_SOURCE;
  const originId = source.id || '';
  const childId = source.childId || '';
  const name = normalizeText(nextMedication.name || suggestion.name);
  const reviewLabel = name || (suggestion.planAction === 'needs_clarification' ? 'Medication name needed' : '');
  const sourceText = normalizeText(suggestion.sourceText || source.rawText || source.text);
  const sourceAnchor = suggestion.id || candidate.id || sourceText;
  const shell = originId
    ? buildReviewableCandidateShell({
      origin: buildReviewableOrigin({
        type: originType,
        id: originId,
        collection: REVIEWABLE_ORIGIN_COLLECTIONS.CARE_SOURCES,
        childId,
        sourceText,
      }),
      domain: MEDICATION_REVIEW_DOMAIN,
      label: reviewLabel,
      sourceAnchor,
      sourceText,
      allowedActions: MEDICATION_REVIEW_ALLOWED_ACTIONS,
      suggestedAction: candidate.suggestedAction || 'add_new',
      confidence: suggestion.confidence || '',
    })
    : {
      schemaVersion: 1,
      key: buildCandidateKey({
        originId,
        domain: MEDICATION_REVIEW_DOMAIN,
        label: reviewLabel,
        sourceAnchor,
      }),
      origin: {
        type: originType,
        id: originId,
        collection: REVIEWABLE_ORIGIN_COLLECTIONS.CARE_SOURCES,
        childId,
      },
      domain: MEDICATION_REVIEW_DOMAIN,
      label: name,
      sourceText,
      confidence: suggestion.confidence || '',
      needsReview: true,
      suggestedAction: candidate.suggestedAction || 'add_new',
      allowedActions: MEDICATION_REVIEW_ALLOWED_ACTIONS,
    };

  return {
    ...shell,
    candidateKey: shell.candidateKey || shell.key,
    originType,
    originId,
    childId,
    candidateId: candidate.id || suggestion.id || '',
    reviewReasons: [
      ...(Array.isArray(suggestion.reviewReasons) ? suggestion.reviewReasons : []),
      ...(Array.isArray(nextMedication.reviewReasons) ? nextMedication.reviewReasons : []),
    ],
    extracted: {
      name,
      dose,
      unit,
      rawDose: [dose, unit].filter(Boolean).join(' '),
      time,
      scheduleText,
      sourceText,
      status: normalizeText(nextMedication.sourceStatus || nextMedication.status || suggestion.status || 'Active'),
      form: normalizeText(nextMedication.form || suggestion.form),
      notes: normalizeText(nextMedication.notes || suggestion.notes || suggestion.purpose),
      medication: {
        ...nextMedication,
        schedules: schedules.map((schedule) => ({ ...schedule })),
        sourceDocuments: Array.isArray(nextMedication.sourceDocuments)
          ? nextMedication.sourceDocuments.map((document) => ({ ...document }))
          : [],
        planHistory: Array.isArray(nextMedication.planHistory)
          ? nextMedication.planHistory.map((entry) => ({ ...entry }))
          : [],
        history: Array.isArray(nextMedication.history)
          ? nextMedication.history.map((entry) => ({ ...entry }))
          : [],
      },
      reason: normalizeText(suggestion.changeReason || suggestion.purpose),
      planAction: normalizeText(suggestion.planAction),
      temporaryHold: suggestion.temporaryHold && typeof suggestion.temporaryHold === 'object'
        ? { ...suggestion.temporaryHold }
        : null,
      discontinuation: suggestion.discontinuation && typeof suggestion.discontinuation === 'object'
        ? { ...suggestion.discontinuation }
        : null,
      completedCourse: suggestion.completedCourse && typeof suggestion.completedCourse === 'object'
        ? { ...suggestion.completedCourse }
        : null,
      canonicalMedicationRecord: suggestion.canonicalMedicationRecord?.schemaVersion === 1
        ? readCanonicalMedicationRecord(suggestion)
        : null,
      historicalSnapshot: suggestion.historicalSnapshot && typeof suggestion.historicalSnapshot === 'object'
        ? {
            ...suggestion.historicalSnapshot,
            medications: Array.isArray(suggestion.historicalSnapshot.medications)
              ? suggestion.historicalSnapshot.medications.map((medication) => ({ ...medication }))
              : [],
          }
        : null,
    },
  };
};

export const buildMedicationDraftFromCandidate = (candidate = {}) => {
  const extracted = candidate.extracted || {};
  const extractedMedication = extracted.medication && typeof extracted.medication === 'object'
    ? extracted.medication
    : null;
  const parsedDose = parseMedicationDoseText([extracted.dose, extracted.unit].filter(Boolean).join(' ') || extracted.rawDose);
  const dose = normalizeText(parsedDose.dose || extracted.dose);
  const unit = normalizeText(parsedDose.unit || extracted.unit) || 'mg';
  const sourceNameCandidate = getMedicationSourceNameCandidates(extracted.sourceText)[0] || '';
  const draftName = cleanMedicationCandidateName(extracted.name) || cleanMedicationCandidateName(sourceNameCandidate);
  const time = normalizeText(extracted.time || extracted.scheduleText);

  if (extractedMedication) {
    return {
      ...extractedMedication,
      name: draftName
        ? draftName.charAt(0).toUpperCase() + draftName.slice(1)
        : normalizeText(extractedMedication.name),
      dose: normalizeText(extractedMedication.dose || dose),
      unit: normalizeText(extractedMedication.unit || unit) || 'mg',
      schedules: Array.isArray(extractedMedication.schedules)
        ? extractedMedication.schedules.map((schedule) => ({ ...schedule }))
        : [],
      sourceDocuments: Array.isArray(extractedMedication.sourceDocuments)
        ? extractedMedication.sourceDocuments.map((document) => ({ ...document }))
        : [],
      planHistory: Array.isArray(extractedMedication.planHistory)
        ? extractedMedication.planHistory.map((entry) => ({ ...entry }))
        : [],
      history: Array.isArray(extractedMedication.history)
        ? extractedMedication.history.map((entry) => ({ ...entry }))
        : [],
      temporaryHold: extracted.temporaryHold && typeof extracted.temporaryHold === 'object'
        ? { ...extracted.temporaryHold }
        : extractedMedication.temporaryHold || null,
    };
  }

  return {
    name: draftName
      ? draftName.charAt(0).toUpperCase() + draftName.slice(1)
      : '',
    dose,
    unit,
    form: normalizeText(extracted.form),
    status: normalizeText(extracted.status),
    notes: normalizeText(extracted.notes) || 'Added from a medication mention.',
    schedules: [{
      id: 'extracted-medication-review-0',
      dose,
      unit,
      time,
    }],
    sourceStatus: 'extracted_medication_review',
    temporaryHold: extracted.temporaryHold && typeof extracted.temporaryHold === 'object'
      ? { ...extracted.temporaryHold }
      : null,
  };
};

export const getMedicationReviewCandidateMatches = (candidate = {}, medications = []) => {
  const extracted = candidate.extracted || {};
  const targetName = normalizeMedicationName(extracted.name);
  const targetDose = normalizeComparableText([extracted.dose, extracted.unit].filter(Boolean).join(' '));

  if (!targetName && !extracted.sourceText) {
    return {
      exactMatch: null,
      possibleMatches: [],
    };
  }

  const activeMedications = (Array.isArray(medications) ? medications : []).filter((medication) => (
    medication && !medication.isArchived
  ));
  const rankedMatches = targetName
    ? rankMedicationMatchesForName({ name: targetName, medications: activeMedications })
    : rankMedicationMatchesForSourceText({ sourceText: extracted.sourceText, medications: activeMedications });
  const exactMatches = rankedMatches.filter((match) => match.exact);
  const doseMatches = exactMatches.filter((match) => {
    if (!targetDose) {
      return true;
    }

    const medication = match.entity;
    const schedule = Array.isArray(medication.schedules) ? medication.schedules[0] : null;
    const medicationDose = normalizeComparableText([
      schedule?.dose || medication.dose,
      schedule?.unit || medication.unit,
    ].filter(Boolean).join(' '));

    return !medicationDose || medicationDose === targetDose;
  });
  const exactMatch = (targetDose ? doseMatches[0] : exactMatches[0]) || exactMatches[0] || null;
  const possibleMatches = rankedMatches
    .filter((match) => !exactMatch || match.entity.id !== exactMatch.entity.id)
    .map((match) => ({
      medication: match.entity,
      matchedName: match.matchedName,
      extractedName: match.extractedName || extracted.name,
      score: match.score,
      exact: match.exact,
    }));

  return {
    exactMatch: exactMatch?.entity || null,
    possibleMatches,
  };
};
